<template>
  <Page>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">แกลเลอรีภาพ</div>
          </div>
          <div class="actions">
            <button
              @click.stop="onCreateGalleryButtonClicked"
              class="btn btn-primary">
              <i class="fas fa-plus icon"></i> สร้างแกลเลอรีใหม่
            </button>
          </div>
        </div>
      </div>
    </template>
    <CreateGalleryModal
      v-if="galleryActiveModal === 'CreateGalleryModal'" />
    <div class="container">
      <PreloaderSection
        v-if="loadGalleriesState.isLoading"/>
      <div v-if="!loadGalleriesState.isLoading">
        <!-- render content -->
        <Section
          v-if="!galleriesLoadingError">
          <template v-slot:section-filter>
            <input
              class="form-control form-control-search"
              placeholder="ค้นหาด้วยชื่อแกลเลอรี..."
              :value="filter.q"
              @keyup.enter.stop.prevent="onSearch"/>
          </template>
          <!-- render content -->
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-image">
                    <div class="list-item list-item-primary"></div>
                  </div>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary">ชื่อแกลเลอรี</div>
                    <div class="list-item">สร้างวันที่</div>
                    <div class="list-item">แก้ไขแล้ว</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div
                    :class="`list-item-container no-underline
                    ${deleteGalleryState.isDeleting === item.id ? ' deleting' : ''}`"
                    v-for="(item, itemIndex) in galleries.data"
                    :key="item.id"
                    @click.stop="goToPageDetail(`/galleries/${item.id}`, itemIndex, $event)">
                    <div class="list-item-image">
                      <ImageView
                        :source="item.coverImage ? item.coverImage : null"
                        imageSize="cover160w90h" />
                    </div>
                    <div class="list-item-detail">
                      <div
                        v-if="deleteGalleryState.isDeleting === item.id"
                        class="list-item list-item-primary list-item-title">
                        กำลังลบแกลเลอรี
                      </div>
                      <div
                        v-if="deleteGalleryState.isDeleting !== item.id"
                        class="list-item list-item-primary list-item-title">
                        {{item.title ? item.title : 'Untitled Album'}}
                      </div>
                      <div class="list-item">
                        {{deleteGalleryState.isDeleting === item.id ? '' : formatDateTime(item.createdAt)}}
                      </div>
                      <div class="list-item">
                        {{deleteGalleryState.isDeleting === item.id ? '' : formatDateTime(item.updatedAt)}}
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <DoubleTabToConfirmButton
                            :disabled="deleteGalleryState.isDeleting === item.id"
                            @confirm="onDeleteConfirm(item.id)"/>
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/galleries/${item.id}`">
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div
                class="pagination-container"
                v-if="isShowPagination()">
                <Pagination
                  :lastPage="galleries.lastPage"/>
              </div>
            </div>
            <!-- search result -->
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบแกลเลอรีที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch">
                ล้างผลการค้นหา
              </button>
            </EmptyMessage>
            <!-- list result -->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่พบแกลเลอรี</h3>
              <router-link
                :to="'/galleries/new'"
                class="btn btn-light btn-lg mt-4">
                <i class="fas fa-plus icon"></i> สร้างแกลเลอรีใหม่
              </router-link>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <ErrorMessageGalleryContainer
          v-if="galleriesLoadingError"
          :error="loadGalleriesState.error"/>
      </div>
    </div>
  </Page>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import dateFormatter from '@/utils/dateFormatter';
import Page from '@/layouts/Page.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import Pagination from '@/components/Pagination.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import DoubleTabToConfirmButton from '@/components/DoubleTapToConfirmButton.vue';
import ImageView from '@/image/ImageView.vue';

import ErrorMessageGalleryContainer from './components/ErrorMessageGalleryContainer.vue';
import CreateGalleryModal from './modals/CreateGalleryModal.vue';

export default {
  mixins: [dateFormatter],
  components: {
    CreateGalleryModal,
    Page,
    Pagination,
    ImageView,
    Section,
    List,
    EmptyMessage,
    PreloaderSection,
    ErrorMessageGalleryContainer,
    DoubleTabToConfirmButton,
  },
  computed: {
    ...mapGetters([
      'deleteGalleryState',
      'galleries',
      'galleryActiveModal',
      'loadGalleriesState',
    ]),
    filter() {
      return this.$route.query;
    },
    galleriesLoadingError() {
      return this.loadGalleriesState.error.statusCode;
    },
  },
  watch: {
    filter(newValue) {
      this.loadAllGalleries(newValue);
    },
    galleries() {
      this.loadAllGalleries();
    },
  },
  created() {
    this.loadAllGalleries(this.filter);
  },
  methods: {
    ...mapActions([
      'deleteCurrentGallery',
      'loadAllGalleries',
      'setGalleryActiveModal',
      'resetAllGalleryState',
    ]),
    async onDeleteConfirm(galleryId) {
      await this.deleteCurrentGallery(galleryId);
    },
    isEmpty() {
      return _.isEmpty(this.galleries.data) && _.every(this.filter, _.isEmpty);
    },
    isEmptySearch() {
      return _.isEmpty(this.galleries.data) && !_.every(this.filter, _.isEmpty);
    },
    onSearch(event) {
      if (event.target.value) {
        const filter = { ...this.filter, q: event.target.value };
        this.$router.push({ query: { ...filter } });
      }
    },
    clearSearch() {
      const filter = null;
      this.$router.push({ query: { ...filter } });
    },
    isShowPagination() {
      return this.galleries.lastPage > 1;
    },
    onCreateGalleryButtonClicked() {
      this.setGalleryActiveModal('CreateGalleryModal');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';

.list-item-container {
  opacity: 1;
  transition: opacity 1s ease;
}

.list-item-container.deleting {
  opacity: 0;
}
</style>
